import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import TaskDrag from "../../bryntum-lib/widget/extendingClasses/TaskDrag.jsx";
import BryntumGridWrapper from "../components/BryntumGrid.jsx";
import BryntumSchedulerWrapper from "../components/BryntumScheduler.jsx";
import { BryntumSplitter } from "@bryntum/schedulerpro-react";
import BryntumContext, { getSetting, userPreferences } from "../context/bryntumContext.jsx";
import { disableZoomTimeline, getOriginalRecord, swivuCheckGridGrants } from "../../other/utils/utils.jsx";
import { callOnLoadOnColumns, getScheduleInstanceFromViewManager } from "../../other/utils/otherUtils.jsx";
import useRefsInit from "../hooks/useRefsInit.jsx";
import SwivuSchedulerTimelineSortPlugin from "../../bryntum-lib/plugins/SwivuSchedulerTimelineSortPlugin.jsx";
import SwivuSchedulerMenuPlugin from "../../bryntum-lib/plugins/SwivuSchedulerMenuPlugin.jsx";
import { GRANTS } from "../../other/constants/PrivilegeTypes.jsx";
import SwivuSchedulerEventMenuPlugin from "../../bryntum-lib/plugins/SwivuSchedulerEventMenuPlugin.jsx";
import AuthContext from "../context/authContext.jsx";
import { VIEW_TYPES, ViewType } from "../../other/constants/OtherKeys.jsx";
import { processCellMenuItems } from "../../other/utils/gridCellMenuUtils.jsx";
import AlertNotificationBanner from "../components/alertNotificationBanner/AlertNotificationBanner.jsx";
import { EDITOR_KEYS } from "../../other/constants/EditorKeys.jsx";
import useEnableViewTracking from "../hooks/useEnableViewTracking.jsx";
import { STORE_KEYS } from "../../other/constants/StoreKeys.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../other/constants/PersistenceStateKeys.jsx";
import { removeDuplicateOverlappingEvents } from "../../other/utils/schedulerUtils.jsx";
import { StringHelper } from "@bryntum/schedulerpro";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";
import ViewSpecificHeader from "../components/appHeader/ViewSpecificHeader.jsx";
import { CopyToBoardDialog } from "@/react/components/CopyToBoardDialog";

const SchedulePage = () => {
  const schedulerRef = useRef(null);
  const gridRef = useRef(null);
  const { setRefs, isRefsInit } = useRefsInit([schedulerRef, gridRef]);
  const { user } = useContext(AuthContext);
  const { project, stateProvider, currentViewRef, editorHandler, viewManager, entityManager } = useContext(BryntumContext);
  const [bulkCopyRecords, setBulkCopyRecords] = useState(null);
  useEnableViewTracking(schedulerRef, VIEW_TYPES.resources, isRefsInit, VIEW_TYPES.schedulerView);
  useEnableViewTracking(gridRef, VIEW_TYPES.tasks, isRefsInit, VIEW_TYPES.schedulerView);

  const editorHandlerRef = useRef();
  editorHandlerRef.current = editorHandler;

  const setBarMargin = useCallback(() => {
    if (schedulerRef.current) {
      const viewType = getSetting("viewType");
      schedulerRef.current.instance.barMargin = viewType === ViewType.compact ? 2.5 : viewType === ViewType.compressed ? 1 : 10;
    }
  }, []);

  const setTaskStyle = useCallback(() => {
    if (schedulerRef.current) {
      const taskStyle = getSetting("taskStyle", null, true);
      schedulerRef.current.instance.eventStyle = taskStyle;
      gridRef.current.instance.refreshRows();
    }
  }, []);

  const setTaskColor = useCallback(() => {
    if (schedulerRef.current) {
      const taskColor = getSetting("taskColor", null, true);
      schedulerRef.current.instance.eventStyle = taskColor;
      gridRef.current.instance.refreshRows();
    }
  }, []);

  /** Event Duplication Update */

  // Note: duplication removal currently runs only on the schedule page
  useEffect(() => {
    const schedulerInstance = schedulerRef.current?.instance;
    if (project && schedulerInstance) {
      const onEventDuplicateCheckHandler = (eventRecord, resource) => {
        // Default false = prevent duplicates board-wide
        const enableRedundantTaskEvents =
          userPreferences.getSetting(PERSISTENCE_STATE_KEYS.enableRedundantTaskEvents, "false", true) === "true";
        // resource can be null in case of placing events using the copy down and event being placed is placed on the group header
        if (!enableRedundantTaskEvents && resource) {
          removeDuplicateOverlappingEvents(eventRecord, resource);
        }
      };
      const eventListenerDestroy = project.eventStore.on("update", (e) => {
        // listen to data change only otherwise any other update such as name could initiate the duplicate checks
        if (e.changes?.startDate || e.changes?.endDate) {
          onEventDuplicateCheckHandler(e.record, e.record.resource);
        }
      });
      const assignmentListenerDestroy = project.assignmentStore.on("update", (e) => {
        /**
          * e.record.resource, if group-header then we didn't receive resource, also not getting any property isSpecialRow or
            isGroupHeader, because on event timeline
          * e.record.event, the events get null, when schedule and then immediately remove that before add success, the crash on console,
         */
        if (e.record.resource && e.record.event) {
          onEventDuplicateCheckHandler(e.record.event, e.record.resource);
        }
      });

      return () => {
        eventListenerDestroy();
        assignmentListenerDestroy();
      };
    }
  }, [project]);

  // install listener on userPreferences
  useEffect(() => {
    const zoomOnMousePrefValue = StringHelper.safeJsonParse(userPreferences.getSetting(PERSISTENCE_STATE_KEYS.disableTimelineZoomOnMouse, true));
    disableZoomTimeline(schedulerRef.current.instance, zoomOnMousePrefValue);

    setBarMargin();
    setTaskStyle();
    setTaskColor();

    userPreferences.boardPreferencesStore.on(
      "change",
      ({ record }) => {
        if (record?.key === "taskStyle") {
          setTaskStyle();
        }
        if (record?.key === "taskColor") {
          setTaskColor();
        }
      },
      schedulerRef.current.instance
    );

    userPreferences.on(
      "beforeUpdate",
      (e) => {
        if (e?.record.key === PERSISTENCE_STATE_KEYS.selectedRowHighlight && e.changes.value === "false") {
          // the following operation needs to be done in the `beforeUpdate` listener as doing this in `update` listener will not work as we have beforeSelectionChange logic in SwivuSchedulerPro
          // where we prevent selection change if PERSISTENCE_STATE_KEYS.selectedRowHighlight value is "false"
          schedulerRef.current.instance.deselectAll();
        }
      },
      schedulerRef.current.instance
    );

    /* Handling user preference for bar margin here instead of swivuSchedulerPro, since bar margin setting is exclusive for scheduler page  */
    userPreferences.on(
      "change",
      ({ record }) => {
        if (record?.key === "viewType") setBarMargin();
      },
      schedulerRef.current.instance
    );
  }, [setTaskStyle, setTaskColor]);

  useEffect(() => {
    // Setup dragging
    if (user && user.can(GRANTS.grant_three)) {
      new TaskDrag({
        grid: gridRef.current.instance,
        schedule: schedulerRef.current.instance,
        constrain: false,
        outerElement: gridRef.current.instance.element
      });
    }
  }, [schedulerRef, user]);

  useEffect(() => {
    if (user && project && stateProvider) {
      const schedulerInstance = schedulerRef.current.instance;
      // Assign the correct project instance to the scheduler
      schedulerInstance.project = project;
      schedulerInstance.store.relayAll(schedulerInstance, "swivuResourceStore");
      schedulerInstance.onClickSwivuEditMessage = () => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings' });
      };
      swivuCheckGridGrants(schedulerInstance, user);
      callOnLoadOnColumns(schedulerInstance.columns, project, { editorHandler, user, viewManager });

      // Assign custom project store to the grid
      if (project.extraTaskStore) {
        gridRef.current.instance.store = project.extraTaskStore;
        gridRef.current.instance.store.relayAll(gridRef.current.instance, "swivuTaskStore");
        gridRef.current.instance.features.cellMenu.processItems = processCellMenuItems({
          editorHandler,
          entityName: "Task",
          grid: gridRef.current.instance,
          promptOnRemove: true,
          project,
          tagStoreKey: STORE_KEYS.taskTagValuesStore,
          onBulkCopyTo: (selectedRecords) => setBulkCopyRecords(selectedRecords),
        });
        gridRef.current.instance.onClickSwivuEditMessage = () => {
          viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings' });
        };
        swivuCheckGridGrants(gridRef.current.instance, user);
        callOnLoadOnColumns(gridRef.current.instance.columns, project, {
          editorHandler,
          user,
          scheduler: schedulerInstance,
          stateProvider,
          currentViewRef,
          canDrag: user.can(GRANTS.grant_four),
          entityManager,
          viewManager
        });
      }

      currentViewRef.current.scrollSchedulerTimeAxisToCurrDate = () => {
        const schedulerView = getScheduleInstanceFromViewManager(viewManager);
        if (schedulerView && userPreferences) {
          const isCenterTimelineOnToday = StringHelper.safeJsonParse(userPreferences.getSetting(PERSISTENCE_STATE_KEYS.centerSchedulerLineOnToday, true));
          schedulerView.scrollToNow({
            animate: true,
            block: isCenterTimelineOnToday ? "center" : "start"
          });
          currentViewRef.current.swivuLastViewportCenterDate = schedulerInstance.viewportCenterDate;
        }
      };

      if (!schedulerInstance.hasPlugin(SwivuSchedulerTimelineSortPlugin)) schedulerInstance.addPlugins(SwivuSchedulerTimelineSortPlugin);
      if (!schedulerInstance.hasPlugin(SwivuSchedulerMenuPlugin)) schedulerInstance.addPlugins(SwivuSchedulerMenuPlugin);
      if (!schedulerInstance.hasPlugin(SwivuSchedulerEventMenuPlugin)) schedulerInstance.addPlugins(SwivuSchedulerEventMenuPlugin);

      schedulerInstance.isEventSelectable = (e) => {
        const eventIds = (schedulerInstance.selectedEvents || []).map((event) => event.id);
        return !eventIds.includes(e.id);
      };
    }
  }, [schedulerRef, stateProvider, project, user, editorHandler]);

  /* Keeping these clean up function in effects with other dependencies causes the ref to reset so any editors using it faces causes null, therefore keeping it here */
  useEffect(() => {
    return () => {
      currentViewRef.current.swivuLastViewportCenterDate = null;
    };
  }, [currentViewRef]);

  useEffect(() => {
    const schedulerViewInstance = schedulerRef.current?.instance;
    const taskGridInstance = gridRef.current?.instance;
    if (schedulerViewInstance && taskGridInstance) {
      // When setting the state project is not yet ready, so refresh is scheduled on project
      // refresh event. When refresh event is triggered scheduler validates if there are events
      // visible in the current view and if there are none, it won't actually refresh rows. Therefore
      // we need to force refresh here.
      schedulerViewInstance.refreshRows();
    }
  }, [user]);

  useEffect(() => {
    if (project?.resourceStore) {
      const schedulerViewInstance = schedulerRef.current?.instance;
      schedulerViewInstance.viewManager = viewManager;
      schedulerViewInstance.onClickSwivuUserEditMessage = () => {
        if (user && user.can(GRANTS.grant_four)) {
          viewManager.selectView(viewManager.getDefaultViewByType(VIEW_TYPES.users));
        }
      };
    }
  }, [project, viewManager]);

  return (
    <>
      <ViewSpecificHeader />
      {user && <AlertNotificationBanner project={project} editorHandler={editorHandler} stateProvider={stateProvider} user={user} />}
      {bulkCopyRecords && (
        <CopyToBoardDialog
          open={!!bulkCopyRecords}
          onOpenChange={(isOpen) => { if (!isOpen) setBulkCopyRecords(null); }}
          records={bulkCopyRecords}
          entityType={ENTITY_TYPES.TASK}
        />
      )}
      <div className="flex flex-1">
        {/* Todo: Handle striping globally in scheduler as well */}
        <BryntumSchedulerWrapper
          stateId
          ref={setRefs(schedulerRef)}
          flex="1 1 75%"
          onTaskFind={({ source, point, resourceRecord, eventRecord, date: selectedDate, item }) => {
            const canReplaceTaskEvent = item.up("menuitem").ref === SwivuSchedulerMenuPlugin.itemsKeyRef.replaceTask;
            editorHandler.openEditor(EDITOR_KEYS.taskFinderEditor, null, {
              point,
              resourceRecord,
              eventRecord: canReplaceTaskEvent ? eventRecord : null,
              selectedDate,
              scheduler: source,
              canInsert: user.can(GRANTS.grant_four),
              canDrag: user.can(GRANTS.grant_four),
              onTaskClick: ({ record }) => {
                const originalRecord = getOriginalRecord(record);
                entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: originalRecord.id });
              }
            }, {
              by: "recordType",
              useExisting: true
            });
          }}
          searchFeature={true}
        />
        {/* in bryntum upgrade 7.1 the splitter has too much z index which causes it to appear on top of task dash UI*/}
        <BryntumSplitter cls="after:z-30" />
        <BryntumGridWrapper
          stateId
          ref={setRefs(gridRef)}
          flex="1 1 25%"
          onTaskFind={({ point }) => {
            editorHandler.openEditor(EDITOR_KEYS.taskFinderEditor, null, {
              point,
              scheduler: schedulerRef.current.instance,
              canDrag: user.can(GRANTS.grant_four),
              onTaskClick: ({ record }) => {
                const originalRecord = getOriginalRecord(record);
                entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: originalRecord.id });
              }
            }, {
              by: "recordType",
              useExisting: true
            });
          }}
          searchFeature={true}
        />
      </div>
    </>
  );
};

export default SchedulePage;
